import { useState, useEffect } from "react";
import { Plus, Pencil, Trash2, Eye, EyeOff, ChevronDown, ChevronUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { api } from "@/lib/api";

const DEFAULT_CATEGORIES = [
  "General",
  "Shipping",
  "Returns & Exchanges",
  "Payments",
  "Orders",
  "Products",
];

export default function AdminFAQ() {
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingFaq, setEditingFaq] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [form, setForm] = useState({
    question: "",
    answer: "",
    category: "General",
    isActive: true,
    order: 0,
  });

  useEffect(() => {
    loadFaqs();
  }, []);

  async function loadFaqs() {
    try {
      const res = await api.get("/faqs?all=true");
      setFaqs(res.data);
    } catch (err) {
      toast.error("Failed to load FAQs");
    } finally {
      setLoading(false);
    }
  }

  function openCreate() {
    setEditingFaq(null);
    setForm({
      question: "",
      answer: "",
      category: "General",
      isActive: true,
      order: faqs.length,
    });
    setDialogOpen(true);
  }

  function openEdit(faq) {
    setEditingFaq(faq);
    setForm({
      question: faq.question,
      answer: faq.answer,
      category: faq.category || "General",
      isActive: faq.isActive,
      order: faq.order || 0,
    });
    setDialogOpen(true);
  }

  async function handleSave() {
    if (!form.question.trim()) {
      toast.error("Question is required");
      return;
    }
    if (!form.answer.trim()) {
      toast.error("Answer is required");
      return;
    }

    try {
      if (editingFaq) {
        await api.put(`/faqs/${editingFaq.id}`, form);
        toast.success("FAQ updated");
      } else {
        await api.post("/faqs", form);
        toast.success("FAQ created");
      }
      setDialogOpen(false);
      loadFaqs();
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to save FAQ");
    }
  }

  async function handleDelete(faq) {
    if (!confirm(`Delete this FAQ?`)) return;
    try {
      await api.delete(`/faqs/${faq.id}`);
      toast.success("FAQ deleted");
      loadFaqs();
    } catch (err) {
      toast.error("Failed to delete FAQ");
    }
  }

  async function toggleActive(faq) {
    try {
      await api.put(`/faqs/${faq.id}`, { isActive: !faq.isActive });
      loadFaqs();
    } catch (err) {
      toast.error("Failed to update FAQ");
    }
  }

  async function moveUp(faq, index) {
    if (index === 0) return;
    const prev = faqs[index - 1];
    try {
      await api.post("/faqs/reorder", {
        items: [
          { id: faq.id, order: prev.order },
          { id: prev.id, order: faq.order },
        ],
      });
      loadFaqs();
    } catch (err) {
      toast.error("Failed to reorder");
    }
  }

  async function moveDown(faq, index) {
    if (index === faqs.length - 1) return;
    const next = faqs[index + 1];
    try {
      await api.post("/faqs/reorder", {
        items: [
          { id: faq.id, order: next.order },
          { id: next.id, order: faq.order },
        ],
      });
      loadFaqs();
    } catch (err) {
      toast.error("Failed to reorder");
    }
  }

  // Group FAQs by category
  const groupedFaqs = faqs.reduce((acc, faq) => {
    const cat = faq.category || "General";
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(faq);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="p-8">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-secondary rounded w-48" />
          <div className="h-64 bg-secondary rounded" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">FAQs</h1>
          <p className="text-muted-foreground text-sm mt-1">
            Manage frequently asked questions
          </p>
        </div>
        <Button onClick={openCreate}>
          <Plus className="w-4 h-4 mr-2" /> Add FAQ
        </Button>
      </div>

      {Object.keys(groupedFaqs).length === 0 ? (
        <div className="bg-card rounded-xl border border-border p-12 text-center text-muted-foreground">
          No FAQs yet. Click "Add FAQ" to create one.
        </div>
      ) : (
        <div className="space-y-6">
          {Object.entries(groupedFaqs).map(([category, categoryFaqs]) => (
            <div key={category} className="bg-card rounded-xl border border-border overflow-hidden">
              <div className="bg-secondary/30 px-4 py-3 border-b border-border">
                <h2 className="font-semibold text-sm">{category}</h2>
              </div>
              <div className="divide-y divide-border">
                {categoryFaqs.map((faq, index) => (
                  <div
                    key={faq.id}
                    className={`transition-colors ${!faq.isActive ? "opacity-50" : ""}`}
                  >
                    <div className="flex items-start gap-3 px-4 py-3">
                      <div className="flex flex-col gap-1 pt-1">
                        <button
                          onClick={() => moveUp(faq, faqs.indexOf(faq))}
                          disabled={faqs.indexOf(faq) === 0}
                          className="p-1 hover:bg-secondary rounded disabled:opacity-30"
                        >
                          <ChevronUp className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => moveDown(faq, faqs.indexOf(faq))}
                          disabled={faqs.indexOf(faq) === faqs.length - 1}
                          className="p-1 hover:bg-secondary rounded disabled:opacity-30"
                        >
                          <ChevronDown className="w-3.5 h-3.5" />
                        </button>
                      </div>
                      <div className="flex-1 min-w-0">
                        <button
                          onClick={() => setExpandedId(expandedId === faq.id ? null : faq.id)}
                          className="text-left w-full"
                        >
                          <div className="font-medium text-sm">{faq.question}</div>
                        </button>
                        {expandedId === faq.id && (
                          <div className="mt-2 text-sm text-muted-foreground whitespace-pre-wrap">
                            {faq.answer}
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <button
                          onClick={() => toggleActive(faq)}
                          className={`p-2 rounded-lg transition-colors ${
                            faq.isActive
                              ? "text-green-600 hover:bg-green-500/10"
                              : "text-red-500 hover:bg-red-500/10"
                          }`}
                          title={faq.isActive ? "Active" : "Hidden"}
                        >
                          {faq.isActive ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                        </button>
                        <button
                          onClick={() => openEdit(faq)}
                          className="p-2 hover:bg-secondary rounded-lg transition-colors"
                          title="Edit"
                        >
                          <Pencil className="w-4 h-4 text-muted-foreground" />
                        </button>
                        <button
                          onClick={() => handleDelete(faq)}
                          className="p-2 hover:bg-secondary rounded-lg transition-colors text-red-500"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editingFaq ? "Edit FAQ" : "Create FAQ"}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2 col-span-2 sm:col-span-1">
                <Label>Category</Label>
                <Select
                  value={form.category}
                  onValueChange={(v) => setForm({ ...form, category: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {DEFAULT_CATEGORIES.map((cat) => (
                      <SelectItem key={cat} value={cat}>
                        {cat}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2 col-span-2 sm:col-span-1">
                <Label>Display Order</Label>
                <Input
                  type="number"
                  value={form.order}
                  onChange={(e) => setForm({ ...form, order: parseInt(e.target.value) || 0 })}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Question *</Label>
              <Input
                value={form.question}
                onChange={(e) => setForm({ ...form, question: e.target.value })}
                placeholder="e.g., Do you offer returns or exchanges?"
              />
            </div>

            <div className="space-y-2">
              <Label>Answer *</Label>
              <Textarea
                value={form.answer}
                onChange={(e) => setForm({ ...form, answer: e.target.value })}
                placeholder="Detailed answer to the question..."
                rows={6}
              />
            </div>

            <div className="flex items-center gap-3 pt-2">
              <Switch
                checked={form.isActive}
                onCheckedChange={(v) => setForm({ ...form, isActive: v })}
              />
              <Label className="cursor-pointer">Active (visible to public)</Label>
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleSave}>
                {editingFaq ? "Save Changes" : "Create FAQ"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
